import { FieldPath } from '@/modules/dynamic-form/models/FieldPath.model';
import { UnifiedFieldRecord, UnifiedFieldRecordKey } from '@/modules/dynamic-form/models/UnifiedFieldRecord.model';

export function getFieldDataFromUnifiedRecord<T>(
    record: UnifiedFieldRecord<T>,
    fieldPath: FieldPath,
): T | undefined {
    return record[fieldPath]?.$self;
}

export function getSubFieldDataFromUnifiedRecord<T>(
    record: UnifiedFieldRecord<T>,
    fieldPath: FieldPath,
    propPath: string,
): T | undefined {
    return record[fieldPath]?.[propPath];
}

export function createBaseUnifiedFieldRecordValue<T>(
    value: any,
): Record<UnifiedFieldRecordKey, T> {
    return { $self: value };
}

/**
 * Create a unified field record value based on the structure of the field meta value
 * @param structureValue - The structure of the field meta value to analyze
 * @param baseValue - The base value to apply to the record
 * @param prefix - Internal parameter for building compound keys for nested structures
 * @returns Record of field keys to set the base value to
 */
export function createCompoundUnifiedFieldRecordValue<T>(
    structureValue: any,
    baseValue: T,
    prefix = '',
): Record<UnifiedFieldRecordKey, T> {
    const result: Record<UnifiedFieldRecordKey, T> = {};

    // Root level always gets $self
    if (prefix === '') {
        result.$self = baseValue;
    }

    if (structureValue === null || structureValue === undefined) {
        if (prefix !== '') {
            result.$self = baseValue;
        }
        return result;
    }

    if (Array.isArray(structureValue)) {
        if (structureValue.length === 0) {
            // Empty array - set $self (only if not root)
            if (prefix !== '') {
                result.$self = baseValue;
            }
        } else {
            // Non-empty array - process items, don't set $self for the array itself
            structureValue.forEach((item, index) => {
                const key = prefix ? `${prefix}.${index}` : index.toString();

                // For array items, recursively process their structure
                if (typeof item === 'object' && item !== null) {
                    const nestedResult = createCompoundUnifiedFieldRecordValue(item, baseValue, key);
                    Object.entries(nestedResult).forEach(([ nestedKey, nestedValue ]) => {
                        result[nestedKey as UnifiedFieldRecordKey] = nestedValue;
                    });
                } else {
                    // For primitive array items, just set the key to the base value
                    result[key as UnifiedFieldRecordKey] = baseValue;
                }
            });
        }
        return result;
    }

    if (typeof structureValue === 'object') {
        const keys = Object.keys(structureValue);
        if (keys.length === 0) {
            // Empty object - set $self (only if not root)
            if (prefix !== '') {
                result.$self = baseValue;
            }
        } else {
            // Non-empty object - process properties, don't set $self for the object itself
            keys.forEach((key) => {
                const item = structureValue[key];
                const compoundKey = prefix ? `${prefix}.${key}` : key;

                // Recursively process nested structures
                if (typeof item === 'object' && item !== null) {
                    const nestedResult = createCompoundUnifiedFieldRecordValue(item, baseValue, compoundKey);
                    Object.entries(nestedResult).forEach(([ nestedKey, nestedValue ]) => {
                        result[nestedKey as UnifiedFieldRecordKey] = nestedValue;
                    });
                } else {
                    // For primitive object properties, just set the key to the base value
                    result[compoundKey as UnifiedFieldRecordKey] = baseValue;
                }
            });
        }
        return result;
    }

    // For primitive values - set $self (only if not root)
    if (prefix !== '') {
        result.$self = baseValue;
    }
    return result;
}
